/**
 * rqc.repository.ts — RQC 온디맨드 조회 격리 layer.
 *
 * 조회 흐름 (패스스루 — 2026-06-19):
 *   1) rqc_requests/{auto} 문서 생성 (status:"pending", date, requested_by)
 *   2) rqcFetchOnDemand CF가 onCreate 트리거 — yejung SQL 조회
 *   3) CF가 요청 문서에 status:"done" + shipments(inline 배열) 업데이트
 *   4) onSnapshot으로 결과 대기 → 응답 문서의 shipments 를 그대로 사용 (daily_shipment 재조회 없음)
 *   5) 읽고 나면 요청 문서 삭제 (누적 방지)
 *
 * 옛 방식은 CF가 daily_shipment(단수) N건을 저장하고 클라가 readByDate 로 되읽었으나,
 * 폴링마다 N건 쓰기/되읽기가 낭비라 라이브 경로는 패스스루로 전환. daily_shipment(단수)
 * raw 의 유지·집계(daily_shipments 복수)는 cron/집계 갱신이 담당. readByDate 는 CF 실패 시
 * 폴백 캐시로만 사용.
 *
 * GCP 조직 정책이 onCall allUsers를 차단하므로 Firestore 트리거 우회 패턴 필수.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  where,
  type DocumentData,
  type DocumentReference,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { tcol } from "@/domain/tenantPath";
import type { DetailSource } from "@/domain/shipmentDetailRefresh";
import type { RqcShipment, ShipmentDetail } from "@/domain/rqc";

const REQ_COL = "rqc_requests";
const SHIPMENT_COL = "daily_shipment";
const DETAILS_CACHE_COL = "daily_shipment_details";

const DEFAULT_TIMEOUT_MS = 45_000;

/** ko-KR HH:MM:SS — 마지막 갱신 표시용 */
function _hms(d: Date): string {
  return d.toLocaleTimeString("ko-KR", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function _s(v: unknown): string {
  return v == null ? "" : String(v);
}
function _n(v: unknown): number {
  const n = parseFloat(String(v));
  return isNaN(n) ? 0 : n;
}

export function fromFirestore(snap: QueryDocumentSnapshot<DocumentData>): RqcShipment {
  const r = snap.data();
  const tierRaw = _s(r.tier);
  const idxN = typeof r.idx === "number" ? r.idx : undefined;
  const scN = typeof r.sc === "number" && r.sc > 0
    ? r.sc
    : (idxN !== undefined ? idxN + 1 : undefined);
  return {
    id: snap.id,
    date: _s(r.date),
    idx: idxN,
    sc: scN,
    company: _s(r.company),
    site: _s(r.site),
    kind: _s(r.kind),
    code: _s(r.code || r.mix_code),
    mix: _s(r.mix),
    qty: _n(r.qty),
    shipped: _n(r.shipped),
    remain: _n(r.remain),
    cumcars: _n(r.cumcars),
    firstcar: _s(r.firstcar),
    lastcar: _s(r.lastcar),
    memo1: _s(r.memo1),
    status: _s(r.status),
    tier: tierRaw === "gov" || tierRaw === "cust" ? tierRaw : undefined,
    scode: _s(r.scode),
    hcode: _s(r.hcode),
    bcode: _s(r.bcode),
  };
}

/**
 * 응답 문서 inline 배열의 한 원소(plain object) → RqcShipment.
 * fromFirestore 와 같은 매핑이되, 문서 id 대신 `${date}_${sc}` 로 안정 id 합성.
 * (행 React key·정렬 안정성용 — 단수 문서가 없으므로 직접 만든다)
 */
function _coerceInlineShipment(raw: unknown, dateStr: string): RqcShipment | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const tierRaw = _s(r.tier);
  const scN = typeof r.sc === "number" && r.sc > 0 ? r.sc : undefined;
  const idxN = scN !== undefined ? scN - 1 : (typeof r.idx === "number" ? r.idx : undefined);
  return {
    id: `${dateStr}_${scN ?? idxN ?? ""}`,
    date: dateStr,
    idx: idxN,
    sc: scN,
    company: _s(r.company),
    site: _s(r.site),
    kind: _s(r.kind),
    code: _s(r.code || r.mix_code),
    mix: _s(r.mix),
    qty: _n(r.qty),
    shipped: _n(r.shipped),
    remain: _n(r.remain),
    cumcars: _n(r.cumcars),
    firstcar: _s(r.firstcar),
    lastcar: _s(r.lastcar),
    memo1: _s(r.memo1),
    status: _s(r.status),
    tier: tierRaw === "gov" || tierRaw === "cust" ? tierRaw : undefined,
    scode: _s(r.scode),
    hcode: _s(r.hcode),
    bcode: _s(r.bcode),
  };
}

export interface FetchResult {
  shipments: RqcShipment[];
  /** "live" = CF 조회 성공 / "cached" = CF 실패 후 Firestore 캐시 사용 */
  mode: "live" | "cached";
  fetchedAt: string;
  /** 자료 시점 — live = 받은 시각, cached = 캐시 문서가 마지막으로 저장된 시각(synced_at, 없으면 null) */
  dataAt: Date | null;
  error?: string;
}

/** 캐시 문서(`daily_shipment_details/{date}_{sc}`) 스냅샷 */
export interface DetailsCacheSnapshot {
  source: DetailSource | null;
  details: ShipmentDetail[];
  fetchedAt: Date | null;
  hyun: string;
  /** 캐시 문서 존재 여부 — false면 아직 한 번도 조회 안 됨 */
  exists: boolean;
  /** 사이트 시퀀스 (doc id의 ${date}_${sc}에서 추출, 미상=0) */
  sc: number;
}

function _coerceDetail(raw: unknown): ShipmentDetail | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const seq = Number(r.seq);
  if (!Number.isFinite(seq) || seq <= 0) return null;
  return {
    seq,
    time: _s(r.time),
    carNo: _s(r.carNo),
    driver: _s(r.driver),
    qty: _n(r.qty),
    cumQty: _n(r.cumQty),
    status: _s(r.status),
  };
}

function _coerceCacheSnap(
  snap: DocumentData | null | undefined,
  sc: number = 0,
): DetailsCacheSnapshot {
  if (!snap) return { details: [], fetchedAt: null, hyun: "", exists: false, sc, source: null };
  const rawDetails = Array.isArray(snap.details) ? snap.details : [];
  const details = rawDetails
    .map(_coerceDetail)
    .filter((x): x is ShipmentDetail => x !== null);
  const ts = snap.fetched_at;
  const fetchedAt = ts instanceof Timestamp ? ts.toDate() : null;
  const raw = snap.source;
  const source: DetailSource | null = raw &&
    typeof raw.shipped === "number" && Number.isFinite(raw.shipped) &&
    typeof raw.cumcars === "number" && Number.isFinite(raw.cumcars) &&
    typeof raw.hcode === "string" && typeof raw.scode === "string" && typeof raw.bcode === "string"
    ? { shipped: raw.shipped, cumcars: raw.cumcars, hcode: raw.hcode, scode: raw.scode, bcode: raw.bcode }
    : null;
  return { details, fetchedAt, hyun: _s(snap.hyun), exists: true, sc, source };
}

export const rqcRepo = {
  /**
   * daily_shipment에서 해당 날짜 레코드만 읽기. idx 오름차순 정렬.
   *
   * 빈 row 필터: site/company 둘 다 빈 문자열인 doc은 옛 테스트 데이터 잔재로
   * 보고 제외. 정상 yejung 행은 최소 거래처(company) 또는 현장(site) 중 하나는
   * 채워서 들어옴.
   */
  async readByDate(dateStr: string): Promise<RqcShipment[]> {
    return (await rqcRepo.readCacheByDate(dateStr)).rows;
  },

  /** readByDate + 그날 캐시 문서들의 가장 늦은 synced_at(마지막 수집 시각) */
  async readCacheByDate(dateStr: string): Promise<{ rows: RqcShipment[]; syncedAt: Date | null }> {
    const q = query(collection(db, tcol(SHIPMENT_COL)), where("date", "==", dateStr));
    const snap = await getDocs(q);
    let syncedAt: Date | null = null;
    for (const d of snap.docs) {
      const ts = d.get("synced_at");
      if (ts instanceof Timestamp && (!syncedAt || ts.toMillis() > syncedAt.getTime())) syncedAt = ts.toDate();
    }
    const rows = snap.docs
      .map(fromFirestore)
      .filter((r) => r.site.trim() !== "" || r.company.trim() !== "");
    rows.sort((a, b) => (a.idx ?? 0) - (b.idx ?? 0));
    return { rows, syncedAt };
  },

  /**
   * 날짜 범위 [startDate..endDate] 의 daily_shipment(단수) 일괄 읽기 (단일 필드 range 쿼리).
   *
   * 기간 사용량(과거 실적) 조회용 — 과거 영업일은 cron/backfill 이 단수 raw 를 유지하므로
   * CF 호출 없이 Firestore 읽기만으로 충분(저렴). date asc, 같은 날짜 안 idx asc 정렬.
   * 빈 row(거래처·현장 둘 다 빔)는 제외.
   */
  async readBetween(startDate: string, endDate: string): Promise<RqcShipment[]> {
    const q = query(
      collection(db, tcol(SHIPMENT_COL)),
      where("date", ">=", startDate),
      where("date", "<=", endDate),
    );
    const snap = await getDocs(q);
    const rows = snap.docs
      .map(fromFirestore)
      .filter((r) => r.site.trim() !== "" || r.company.trim() !== "");
    rows.sort((a, b) =>
      a.date === b.date ? (a.idx ?? 0) - (b.idx ?? 0) : a.date < b.date ? -1 : 1,
    );
    return rows;
  },

  /**
   * RQC 서버 조회 요청 + 완료 대기 + 결과 읽기.
   * CF 실패 시 자동으로 캐시 폴백.
   */
  async fetchOnDemand(
    dateStr: string,
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ): Promise<FetchResult> {
    const user = auth.currentUser;
    if (!user) throw new Error("로그인이 필요합니다.");

    let reqRef: DocumentReference | null = null;
    try {
      // 1) 요청 문서 생성 → CF가 onCreate 트리거
      reqRef = await addDoc(collection(db, tcol(REQ_COL)), {
        date: dateStr,
        status: "pending",
        requested_at: serverTimestamp(),
        requested_by: user.uid,
      });

      // 2) CF 완료 대기 → 응답 문서의 shipments(inline 배열)를 그대로 사용 (패스스루)
      const ref = reqRef;
      const shipments = await new Promise<RqcShipment[]>((resolve, reject) => {
        const tid = setTimeout(() => {
          unsub();
          reject(new Error(`RQC 조회 타임아웃 (${Math.round(timeoutMs / 1000)}초)`));
        }, timeoutMs);
        const unsub = onSnapshot(
          ref,
          (snap) => {
            const d = snap.data();
            if (!d) return;
            if (d.status === "done") {
              clearTimeout(tid);
              unsub();
              const raw = Array.isArray(d.shipments) ? d.shipments : [];
              resolve(
                raw
                  .map((x: unknown) => _coerceInlineShipment(x, dateStr))
                  .filter((s: RqcShipment | null): s is RqcShipment => s !== null),
              );
            } else if (d.status === "error") {
              clearTimeout(tid);
              unsub();
              reject(new Error(_s(d.error) || "RQC 조회 실패"));
            }
          },
          (err) => {
            clearTimeout(tid);
            reject(err);
          },
        );
      });

      const now = new Date();
      return {
        shipments,
        mode: "live",
        fetchedAt: _hms(now),
        dataAt: now,
      };
    } catch (cfErr) {
      // CF 실패 → Firestore 캐시 폴백 (cron 이 써둔 daily_shipment 단수)
      const { rows: shipments, syncedAt } = await rqcRepo.readCacheByDate(dateStr);
      const msg = cfErr instanceof Error ? cfErr.message : String(cfErr);
      return {
        shipments,
        mode: "cached",
        fetchedAt: syncedAt ? _hms(syncedAt) : "",
        dataAt: syncedAt,
        error: msg,
      };
    } finally {
      // 요청 문서 정리 — 응답 배열을 실어 커졌으므로 누적 방지. 실패해도 무시.
      if (reqRef) void deleteDoc(reqRef).catch(() => {});
    }
  },

  /**
   * 현장별 송장 명세 캐시 구독 (cache-first 패턴).
   *
   * `daily_shipment_details/{date}_{sc}` 문서를 onSnapshot으로 구독.
   * CF가 view.jsp 새로 가져와 같은 문서를 update하면 콜백이 다시 호출됨.
   *
   * 캐시가 없으면 exists:false 로 즉시 한 번 호출되고, 이후 CF가 첫 결과를
   * 쓸 때까지 추가 호출 없음.
   *
   * @returns unsubscribe 함수
   */
  subscribeDetailsCache(
    dateStr: string,
    sc: number,
    onUpdate: (snap: DetailsCacheSnapshot) => void,
    onError?: (err: Error) => void,
  ): () => void {
    const docId = `${dateStr}_${sc}`;
    const ref = doc(db, tcol(DETAILS_CACHE_COL), docId);
    return onSnapshot(
      ref,
      (snap) => {
        onUpdate(_coerceCacheSnap(snap.exists() ? snap.data() : null, sc));
      },
      (err) => onError?.(err),
    );
  },

  /**
   * 특정 날짜의 모든 daily_shipment_details 캐시 구독.
   * doc id = `${date}_${sc}` 패턴이라 documentId range 쿼리로 한 번에 가져옴.
   * 새 사이트 detail이 캐시되면 자동 업데이트 (탕수 통계 갱신용).
   */
  subscribeDetailsByDate(
    dateStr: string,
    cb: (snaps: DetailsCacheSnapshot[], fromCache: boolean) => void,
    onError?: (err: Error) => void,
  ): Unsubscribe {
    const q = query(
      collection(db, tcol(DETAILS_CACHE_COL)),
      where(documentId(), ">=", `${dateStr}_`),
      where(documentId(), "<", `${dateStr}_~`),
    );
    return onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => cb(snap.docs.map((d) => {
        // doc id 패턴: ${date}_${sc} — date 부분 길이만큼 잘라낸 뒤 sc 정수 파싱
        const scStr = d.id.length > dateStr.length + 1 ? d.id.slice(dateStr.length + 1) : "";
        const sc = parseInt(scStr, 10) || 0;
        return _coerceCacheSnap(d.data(), sc);
      }), snap.metadata.fromCache),
      (err) => onError?.(err),
    );
  },

  /**
   * 현장별 송장 명세를 RQC 서버에서 새로 가져오라고 요청.
   *
   *   1) rqc_requests/{auto} 문서 생성 — mode:"details", date, sc, hyun, hcode, scode
   *   2) rqcFetchOnDemand CF가 songjang SQL 조회 → daily_shipment_details에 upsert
   *   3) CF가 요청 문서 status:"done"/"error" 업데이트
   *   4) 본 메서드는 status 완료 신호만 대기 (실제 데이터는 캐시 구독으로 수신)
   *
   * SQL 전환 후 명세는 현장 코드(hcode)+거래처 코드(scode)로 songjang을 조회한다.
   * sc/hyun 은 캐시 docid(`${date}_${sc}`)·표시용으로 계속 사용.
   * 호출 측에서 캐시를 미리 구독하고 있으면 캐시 갱신은 자동으로 반영됨.
   */
  async triggerDetailsRefresh(
    dateStr: string,
    sc: number,
    hyun: string,
    hcode: string,
    scode: string,
    source: DetailSource,
    timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error("로그인이 필요합니다.");
    if (!sc || sc <= 0) throw new Error("sc(현장 시퀀스) 필수");
    if (!hyun) throw new Error("hyun(현장명) 필수");
    if (!hcode || !scode) throw new Error("hcode/scode(현장·거래처 코드) 필수 — SQL 전환 후 명세 조회 키");

    const reqRef = await addDoc(collection(db, tcol(REQ_COL)), {
      date: dateStr,
      mode: "details",
      source,
      sc,
      hyun,
      hcode,
      scode,
      status: "pending",
      requested_at: serverTimestamp(),
      requested_by: user.uid,
    });

    await new Promise<void>((resolve, reject) => {
      const tid = setTimeout(() => {
        unsub();
        reject(new Error(`송장 조회 타임아웃 (${Math.round(timeoutMs / 1000)}초)`));
      }, timeoutMs);
      const unsub = onSnapshot(
        reqRef,
        (snap) => {
          const d = snap.data();
          if (!d) return;
          if (d.status === "done") {
            clearTimeout(tid);
            unsub();
            resolve();
          } else if (d.status === "error") {
            clearTimeout(tid);
            unsub();
            reject(new Error(_s(d.error) || "송장 조회 실패"));
          }
        },
        (err) => {
          clearTimeout(tid);
          reject(err);
        },
      );
    });
  },
};
